<template>
  <v-container fill-height justify-content-start :class="{'pa-2': $vuetify.breakpoint.xsOnly}">
    <v-layout column>
      <v-form ref="form" v-model="valid">
        <v-layout row wrap class="flex-none">
          <v-flex xs12 sm6 class="px-1">
            <v-text-field color="accent" v-model="name" outline :label="$t('form.name')" :rules="required" />
          </v-flex>
          <v-flex xs12 sm6 class="px-1">
            <v-text-field color="accent" v-model="surname" outline :label="$t('form.surname')" :rules="required" />
          </v-flex>
        </v-layout>
        <v-layout row wrap class="flex-none">
          <v-flex xs12 sm6 class="px-1">
            <v-text-field
              color="accent"
              v-model="numberGuests"
              @input="isInt({value: numberGuests, errorVar: 'numberGuestsError'})"
              :error="numberGuestsError"
              outline
              :label="$t('form.numberGuests')"
              :rules="required"
            />
          </v-flex>
          <v-flex xs12 sm6 class="px-1">
            <v-autocomplete
              color="accent"
              :items="facilities"
              item-text="name"
              return-object
              v-model="selectedFacility"
              outline
              :label="$t('form.facility')"
              :rules="required"
            />
          </v-flex>
        </v-layout>
        <v-layout row wrap class="flex-none">
          <v-flex xs12 sm6 class="px-1">
            <v-text-field
              color="accent"
              class="price"
              v-model="price"
              :error="priceError"
              outline
              :label="$t('form.price')"
              :rules="required"
              placeholder="0"
            />
          </v-flex>
          <v-flex xs12 sm3 text-xs-right class="pr-2">
            <div v-if="exchangeCalculation">
              <div class="grey--text text--darken-1 pr-2 caption pt-1">{{$t('form.conversion')}}</div>
              <p class="display-1 pr-2">{{formatAmount(exchangeCalculation)}}</p>
            </div>
          </v-flex>
          <v-flex xs12 sm3 class="px-1">
            <v-autocomplete
              color="accent"
              class="currency"
              :disabled="currencyInFacility === undefined"
              :items="currenciesForOptions"
              return-object
              item-text="name"
              item-value="id"
              outline
              v-model="currencySelected"
              :label="$t('form.payment_currency')"
            />
          </v-flex>
        </v-layout>
        <v-layout row wrap class="flex-none">
          <v-flex xs12 sm6 class="px-1">
            <v-autocomplete
              color="accent"
              v-model="paymentMethod"
              :items="paymentMethods"
              item-text="name"
              item-value="id"
              outline
              :label="$t('form.paymentMethodAction')"
              :rules="required"
            />
          </v-flex>
          <v-flex v-if="noCurrencyExchange">
            <v-alert class="alert" type="warning" :value="true">{{$t('warnings.no_exchange_rate')}}</v-alert>
          </v-flex>
        </v-layout>
        <v-layout row wrap class="flex">
          <v-flex xs12 sm6 class="px-1">
            <v-textarea color="accent" outline maxlength="150" v-model="observations" :label="$t('form.observations')" />
          </v-flex>
          <v-flex xs12 sm6 class="mb-4 d-flex justify-end align-end">
            <div class="text-xs-right">
              <v-btn
                :loading="loading"
                :disabled="!valid"
                @click="validate"
                large
                color="primary"
                class="white--text d-inline-block"
              >{{$t('actions.purchase')}}</v-btn>
            </div>
          </v-flex>
        </v-layout>
      </v-form>
    </v-layout>
  </v-container>
</template>

<script>
import { mapState } from 'vuex'
import moneyFormatMixins from '@/mixins/MoneyFormat'
import { PANEL_LOCATION } from '@/config/environment'
export default {
  name: 'home',
  data () {
    return {
      displayAmount: null,
      currenciesForOptions: [],
      numberGuestsError: false,
      priceError: false,
      currencyInFacility: undefined,
      defaultCurrency: null,
      exchangeRate: undefined,
      exchangeCalculation: null,
      valid: false,
      loading: false,
      required: [
        v => !!v || ''
      ],
      noCurrencyExchange: false
    }
  },
  mixins: [moneyFormatMixins],
  computed: {
    ...mapState(['form', 'currencies', 'facilities', 'paymentMethods', 'facility']),
    name: {
      get () {
        return this.form ? this.form.name : null
      },
      set (value) {
        this.$store.commit('setField', { field: 'name', value })
      }
    },
    surname: {
      get () {
        return this.form ? this.form.surname : null
      },
      set (value) {
        this.$store.commit('setField', { field: 'surname', value })
      }
    },
    facilitySelected: {
      get () {
        return this.form ? this.form.facilitySelected : null
      },
      set (value) {
        this.$store.commit('setField', { field: 'facilitySelected', value })
      }
    },
    numberGuests: {
      get () {
        return this.form ? this.form.numberGuests : null
      },
      set (value) {
        this.$store.commit('setField', { field: 'numberGuests', value })
      }
    },
    price: {
      get () {
        return this.displayAmount
      },
      set (value) {
        if (typeof value !== 'undefined' && value !== '') {
          this.setAmount(value)
        } else {
          this.displayAmount = null
          this.$store.commit('setField', {
            field: 'amount',
            value: null
          })
          this.updatePrices()
        }
      }
    },
    paymentMethod: {
      get () {
        return this.form ? this.form.paymentMethod : null
      },
      set (value) {
        this.$store.commit('setField', { field: 'paymentMethod', value })
      }
    },
    selectedFacility: {
      get () {
        return this.facility || null
      },
      set (value) {
        this.$store.commit('setFacility', value)
        this.currencyInFacility = this.filteredCurrencies.find(x => x.id === value.baseCurrencyId)
        this.$store.commit('setField', { field: 'currencyInFacility', value: this.currencyInFacility.id })
        this.applyStyle(this.generateCurrencyStyleDef())
        this.currenciesForOptions = this.filteredCurrencies.filter(x => x.id !== this.currencyInFacility.id)
      }
    },
    currencySelected: {
      get () {
        return this.form ? this.form.currencySelected : null
      },
      set (value) {
        this.$store.dispatch('getExchangeRate', value.id).then((currency) => {
          this.$store.commit('setField', { field: 'currencySelected', value })
          this.exchangeRate = currency.exchangeRate
          this.updatePrices(this.price)
          if (this.exchangeRate) {
            this.noCurrencyExchange = false
          } else {
            this.noCurrencyExchange = true
            this.exchangeCalculation = 0
          }
        })
      }
    },
    observations: {
      get () {
        return this.form ? this.form.observations : null
      },
      set (value) {
        this.$store.commit('setField', { field: 'observations', value })
      }
    },
    filteredCurrencies () {
      return this.currencies
        ? this.currencies.filter(c => c.type === 'LEGAL')
        : []
    }
  },
  created () {
    this.$store.commit('setToken', this.$route.params.token)
    this.$store.dispatch('listFacilities')
    this.$store.dispatch('listCurrencies')
    this.$store.dispatch('listPaymentMethods')
  },
  methods: {
    isInt: function (data) {
      this[data.errorVar] = !Number.isInteger(parseInt(data.value))
      return !this[data.errorVar]
    },
    setAmount (newValue) {
      this.displayAmount = ''
      this.$nextTick(() => {
        this.displayAmount = this.sanitizePrice(newValue)
        this.$store.commit('setField', {
          field: 'amount',
          value: this.parsePrice(newValue)
        })
        this.updatePrices()
      })
    },
    applyStyle (styleDef) {
      let style = document.createElement('style')
      style.type = 'text/css'
      document.head.appendChild(style)
      style.innerHTML = styleDef
    },
    generateCurrencyStyleDef () {
      return `
        .v-text-field.price .v-text-field__slot::after {
          content: '${this.currencyInFacility.name ? this.currencyInFacility.name : ''}' !important;
          margin: auto 0 5px 5px !important;
        }
        :not(.v-text-field).price .v-text-field__slot::after {
          content: '${this.currencyInFacility.name ? this.currencyInFacility.name : ''}' !important;
        }
      `
    },
    updatePrices (price) {
      const isInt = this.isInt({ value: this.form.amount, errorVar: 'priceError' })
      if (isInt) {
        this.priceError = false
        if (this.exchangeRate !== undefined) {
          this.exchangeCalculation = this.form.amount / this.exchangeRate.exchangeRate
          this.$store.commit('setField', { field: 'exchangeCalculation', value: this.exchangeCalculation })
        }
      } else {
        this.exchangeCalculation = null
        this.priceError = true
      }
    },
    validate () {
      if (this.$refs.form.validate()) {
        this.loading = true
        this.$store.dispatch('purchaseDaypass')
          .then(() => {
            this.loading = false
            this.$store.commit('setMessage', { text: this.$t('message.complete_main'), color: 'success', timeout: 0 })
            if (window.opener) {
              window.opener.postMessage('refreshData', PANEL_LOCATION)
              window.close()
            }
          })
      }
    }
  }
}
</script>

<style>
.price input {
  text-align: right;
  margin-right: 5px;
  font-size: 30px;
  max-height: 40px;
}
.currency input {
  max-height: 40px;
}
.v-btn {
  border-radius: 7px;
  height: 50px;
  margin-left: auto;
}
.alert {
  margin: 2px 5px !important;
  border-top: none !important;
}
</style>
